import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Button } from "./Button";
import { FormTextarea } from "./FormFields";
import { LinkifiedText } from "./LinkifiedText";

/**
 * Long free text (a note's description, a POI's notes) that reads like text:
 * filled values open as a read view with clickable links, and switch to an
 * auto-growing textarea on click or via the pencil. Empty values are edited
 * directly so there is nothing extra to click.
 */
export function ReadableTextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
}: {
  id: string;
  /** Accessible name; the field has no visible label. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  // Empty values always edit directly. Otherwise the textarea shows only
  // while the user is working in it; focus marks that, so typing into an empty
  // field doesn't flip to the read view after the first character.
  const [userEditing, setUserEditing] = useState(false);
  const [focusOnEdit, setFocusOnEdit] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editing = userEditing || !value.trim();

  useEffect(() => {
    if (!focusOnEdit) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    setFocusOnEdit(false);
  }, [focusOnEdit]);

  const startEditing = () => {
    if (disabled) return;
    setUserEditing(true);
    setFocusOnEdit(true);
  };

  if (editing && !disabled) {
    return (
      <FormTextarea
        ref={textareaRef}
        id={id}
        aria-label={label}
        value={value}
        onChange={onChange}
        onFocus={() => setUserEditing(true)}
        onBlur={() => setUserEditing(false)}
        placeholder={placeholder}
        rows={2}
        autoGrow
      />
    );
  }

  const handleViewClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("a")) return;
    startEditing();
  };

  return (
    <div className="group/readable relative">
      <div
        id={id}
        aria-label={label}
        role="group"
        onClick={handleViewClick}
        className="custom-scrollbar max-h-44 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-subtle py-2 pl-3 pr-10 text-sm leading-relaxed text-primary data-[disabled=false]:cursor-text data-[disabled=false]:hover:ring-1 data-[disabled=false]:hover:ring-border"
        data-disabled={disabled}
      >
        {value.trim() ? (
          <LinkifiedText text={value} />
        ) : (
          <span className="text-tertiary">{placeholder}</span>
        )}
      </div>
      {!disabled && (
        <Button
          variant="icon"
          size="sm"
          icon="edit"
          aria-label={`Edit ${label.toLowerCase()}`}
          onClick={startEditing}
          className="absolute right-1.5 top-1.5"
        />
      )}
    </div>
  );
}
